import type {
  ConfigureSmsInput,
  ISmsRepository,
  SaveSmsTemplateOverrideInput,
  SendSmsInput,
  SendSmsResult,
  SmsConfiguration,
  SmsConfigurationStatus,
  SmsHistoryFilters,
  SmsMessage,
  SmsTemplateLanguage,
  SmsTemplateOverride,
} from '@erp/data-access/interfaces';
import type {
  SmsCategory,
  SmsContextType,
  SmsMessageStatus,
} from '@erp/domain/sms';
import type { TypedSupabaseClient } from '../client';
import { functionError } from './function-error';

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

function nullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function mapConfiguration(value: unknown): SmsConfiguration {
  const row = asRecord(value);
  return {
    provider: 'winsms',
    configured: row['configured'] === true,
    isActive: row['isActive'] === true,
    senderId: nullableString(row['senderId']),
    status: String(row['status'] ?? 'unconfigured') as SmsConfigurationStatus,
    dailySendLimit: Number(row['dailySendLimit'] ?? 500),
    balance: nullableNumber(row['balance']),
    lastTestedAt: nullableString(row['lastTestedAt']),
    lastErrorCode: nullableString(row['lastErrorCode']),
    updatedAt: nullableString(row['updatedAt']),
  };
}

function mapMessage(value: unknown): SmsMessage {
  const row = asRecord(value);
  return {
    id: String(row['id'] ?? ''),
    recipientPhone: String(row['recipientPhone'] ?? ''),
    recipientName: nullableString(row['recipientName']),
    body: String(row['body'] ?? ''),
    category: String(row['category'] ?? 'transactional') as SmsCategory,
    contextType: String(row['contextType'] ?? 'custom') as SmsContextType,
    contextId: nullableString(row['contextId']),
    clientId: nullableString(row['clientId']),
    documentType: nullableString(row['documentType']),
    documentNumber: nullableString(row['documentNumber']),
    consentConfirmed: row['consentConfirmed'] === true,
    metadata: asRecord(row['metadata']),
    status: String(row['status'] ?? 'unknown') as SmsMessageStatus,
    providerReference: nullableString(row['providerReference']),
    providerCode: nullableString(row['providerCode']),
    providerMessage: nullableString(row['providerMessage']),
    credentialVersion: Number(row['credentialVersion'] ?? 1),
    segmentCount: Number(row['segmentCount'] ?? 0),
    encoding: row['encoding'] === 'ucs2' ? 'ucs2' : 'gsm7',
    sentBy: nullableString(row['sentBy']),
    createdAt: String(row['createdAt'] ?? ''),
    attemptedAt: nullableString(row['attemptedAt']),
    sentAt: nullableString(row['sentAt']),
    deliveredAt: nullableString(row['deliveredAt']),
    lastStatusAt: nullableString(row['lastStatusAt']),
    updatedAt: String(row['updatedAt'] ?? ''),
  };
}

function mapTemplateOverride(value: unknown): SmsTemplateOverride {
  const row = asRecord(value);
  return {
    templateKey: String(row['templateKey'] ?? ''),
    language: String(row['language'] ?? 'fr') as SmsTemplateLanguage,
    body: String(row['body'] ?? ''),
    updatedAt: String(row['updatedAt'] ?? ''),
  };
}

export class SupabaseSmsRepository implements ISmsRepository {
  constructor(private readonly supabase: TypedSupabaseClient) {}

  private async invoke(
    action: string,
    body: UnknownRecord = {},
  ): Promise<UnknownRecord> {
    const { data, error } = await this.supabase.functions.invoke(
      'sms-gateway',
      {
        body: { action, ...body },
      },
    );
    if (error) throw await functionError(error, 'sms_gateway_failed');
    return asRecord(data);
  }

  async getConfiguration(): Promise<SmsConfiguration> {
    const response = await this.invoke('get_configuration');
    return mapConfiguration(response['configuration']);
  }

  async configure(input: ConfigureSmsInput): Promise<SmsConfiguration> {
    const response = await this.invoke('configure', {
      apiKey: input.apiKey,
      senderId: input.senderId,
      isActive: input.isActive,
      dailySendLimit: input.dailySendLimit,
    });
    return mapConfiguration(response['configuration']);
  }

  async testConnection(): Promise<SmsConfiguration> {
    const response = await this.invoke('test_connection');
    return mapConfiguration(response['configuration']);
  }

  async send(input: SendSmsInput): Promise<SendSmsResult> {
    const response = await this.invoke('send', {
      idempotencyKey: input.idempotencyKey,
      recipientPhone: input.recipientPhone,
      recipientName: input.recipientName,
      body: input.body,
      category: input.category,
      consentConfirmed: input.consentConfirmed,
      contextType: input.contextType,
      contextId: input.contextId,
      clientId: input.clientId,
      documentType: input.documentType,
      documentNumber: input.documentNumber,
      metadata: input.metadata,
    });
    return {
      message: mapMessage(response['message']),
      deduplicated: response['deduplicated'] === true,
    };
  }

  async listMessages(filters?: SmsHistoryFilters): Promise<SmsMessage[]> {
    const response = await this.invoke('list_messages', { filters });
    return Array.isArray(response['messages'])
      ? response['messages'].map(mapMessage)
      : [];
  }

  async refreshStatus(messageId: string): Promise<SmsMessage> {
    const response = await this.invoke('refresh_status', { messageId });
    return mapMessage(response['message']);
  }

  async listTemplateOverrides(): Promise<SmsTemplateOverride[]> {
    const response = await this.invoke('list_template_overrides');
    return Array.isArray(response['overrides'])
      ? response['overrides'].map(mapTemplateOverride)
      : [];
  }

  async saveTemplateOverride(
    input: SaveSmsTemplateOverrideInput,
  ): Promise<SmsTemplateOverride> {
    const response = await this.invoke('save_template_override', {
      templateKey: input.templateKey,
      language: input.language,
      body: input.body,
    });
    return mapTemplateOverride(response['override']);
  }

  async resetTemplateOverride(
    templateKey: string,
    language: SmsTemplateLanguage,
  ): Promise<void> {
    await this.invoke('reset_template_override', { templateKey, language });
  }
}
